import React, { useState } from 'react';

const GameSelector = ({ games, onGameSelect, onShowLeaderboard, loading }) => {
  const [selectedDifficulties, setSelectedDifficulties] = useState({});

  const handleDifficultySelect = (gameId, difficulty) => {
    setSelectedDifficulties(prev => ({
      ...prev,
      [gameId]: difficulty
    }));
  };

  const handlePlayGame = (game) => {
    const difficulty = selectedDifficulties[game.id] || 'easy';
    onGameSelect(game, difficulty);
  };

  const getGameIcon = (gameType) => {
    switch (gameType) {
      case 'speed_arithmetic':
        return '⚡';
      case 'pattern_recognition':
        return '🧩';
      case 'math_battle':
        return '⚔️';
      case 'digital_abacus':
        return '🧮';
      case 'mental_math_race':
        return '🏁';
      default:
        return '🎮';
    }
  };

  const getGameColor = (gameType) => {
    switch (gameType) {
      case 'speed_arithmetic':
        return 'from-orange-400 to-red-500';
      case 'pattern_recognition':
        return 'from-purple-400 to-indigo-500';
      case 'math_battle':
        return 'from-red-400 to-pink-500';
      case 'digital_abacus':
        return 'from-teal-400 to-cyan-500';
      case 'mental_math_race':
        return 'from-green-400 to-blue-500';
      default:
        return 'from-gray-400 to-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600">Loading games...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="text-6xl mb-4">🧠</div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Choose Your Mental Math Challenge
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Sharpen your math skills with fun, timed games designed to improve your mental calculation speed and accuracy.
        </p>
      </div>

      {/* Games Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        {games.map((game) => (
          <div key={game.id} className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300">
            {/* Game Header */}
            <div className={`bg-gradient-to-r ${getGameColor(game.game_type)} p-6 text-white`}>
              <div className="text-4xl mb-3">{getGameIcon(game.game_type)}</div>
              <h3 className="text-2xl font-bold mb-2">{game.name}</h3>
              <p className="text-white/90 text-sm">{game.description}</p>
            </div>

            {/* Game Content */}
            <div className="p-6">
              {/* Difficulty Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Choose Difficulty:
                </label>
                <div className="space-y-2">
                  {game.difficulty_levels && ['easy', 'medium', 'hard']
                    .filter(level => game.difficulty_levels[level])
                    .map(level => {
                      const config = game.difficulty_levels[level];
                      return (
                    <label key={level} className="flex items-center py-2 cursor-pointer">
                      <input
                        type="radio"
                        name={`difficulty-${game.id}`}
                        value={level}
                        checked={selectedDifficulties[game.id] === level || (!selectedDifficulties[game.id] && level === 'easy')}
                        onChange={() => handleDifficultySelect(game.id, level)}
                        className="mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 font-medium capitalize">{level}</span>
                      <span className="text-gray-500 text-sm ml-2">
                        {config.name || config.description || ''}
                      </span>
                    </label>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => handlePlayGame(game)}
                  className={`flex-1 bg-gradient-to-r ${getGameColor(game.game_type)} text-white py-3 px-4 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Play Now
                </button>
                
                {/* Leaderboard button only renders when a handler is wired up */}
                {typeof onShowLeaderboard === 'function' && (
                  <button
                    onClick={() => onShowLeaderboard(game.id)}
                    className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    title="View Leaderboard"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                    </svg>
                  </button>
                )}
              </div>

              {/* Premium Badge */}
              {game.is_premium && (
                <div className="mt-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Premium
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Coming Soon Games */}
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">🚀 Coming Soon</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl mb-2">🏁</div>
            <h4 className="font-medium text-gray-900">Math Races</h4>
            <p className="text-sm text-gray-600">Compete with friends in real-time</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl mb-2">🎯</div>
            <h4 className="font-medium text-gray-900">Target Practice</h4>
            <p className="text-sm text-gray-600">Hit the target number using operations</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl mb-2">🧩</div>
            <h4 className="font-medium text-gray-900">Math Puzzles</h4>
            <p className="text-sm text-gray-600">Logic and number puzzles</p>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="mt-12 bg-blue-50 rounded-2xl p-8">
        <h3 className="text-xl font-bold text-blue-900 mb-4">💡 Tips for Success</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-blue-800">
          <div className="flex items-start">
            <div className="text-2xl mr-3">⏱️</div>
            <div>
              <h4 className="font-medium">Stay Calm Under Pressure</h4>
              <p className="text-sm text-blue-700">Take a deep breath and focus on accuracy first, then speed.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="text-2xl mr-3">🧮</div>
            <div>
              <h4 className="font-medium">Use Mental Math Tricks</h4>
              <p className="text-sm text-blue-700">Learn shortcuts like doubling, halving, and breaking down numbers.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="text-2xl mr-3">🎯</div>
            <div>
              <h4 className="font-medium">Practice Regularly</h4>
              <p className="text-sm text-blue-700">Daily practice builds math fluency and confidence.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="text-2xl mr-3">🏆</div>
            <div>
              <h4 className="font-medium">Challenge Yourself</h4>
              <p className="text-sm text-blue-700">Gradually increase difficulty as you improve.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameSelector;